import React, { useState, useEffect, useMemo } from "react"
import { makeStyles } from "@material-ui/core/styles"
import {
  Box,
  Link,
  TextField,
  MenuItem,
  InputAdornment,
} from "@material-ui/core"
import SearchIcon from "@material-ui/icons/Search"
import useStore from "src/v2/utils/store"

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    border: "1px solid #f0f0f0",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  field: {
    flex: "1 1 0",
    minWidth: 140,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": {
      height: 40,
    },
    "& .MuiOutlinedInput-input": {
      paddingTop: 10,
      paddingBottom: 10,
    },
    "& .MuiSelect-outlined.MuiSelect-outlined": {
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  clearLink: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}))

export default function SimulationsFilter() {
  const classes = useStyles()

  const simulations = useStore((state) => state.simulations)
  const [searchText, setSearchText] = useState("")

  const [filters, setFilters] = useState({
    function: "All",
    creator: "All",
    passExpections: "All",
    sortBy: "Created",
  })

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearAllFilters = (e) => {
    if (e) e.stopPropagation()
    setSearchText("")
    setFilters({
      function: "All",
      creator: "All",
      passExpections: "All",
      sortBy: "Created",
    })
  }

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (searchText) count++
    if (filters.function !== "All") count++
    if (filters.creator !== "All") count++
    if (filters.passExpections !== "All") count++
    return count
  }, [filters, searchText])

  useEffect(() => {
    useStore
      .getState()
      .setFilteredSimulations(applyFilters(simulations, filters, searchText))
  }, [filters, searchText, simulations])

  const functionOptions = useMemo(
    () => getFunctionOptions(simulations),
    [simulations],
  )
  const creatorOptions = useMemo(
    () => getCreatorOptions(simulations),
    [simulations],
  )

  const renderSelect = (label, key, options) => (
    <TextField
      select
      size="small"
      variant="outlined"
      label={label}
      value={filters[key]}
      onChange={(e) => updateFilter(key, e.target.value)}
      className={classes.field}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <Box className={classes.filterContainer}>
      <Box className={classes.row}>
        <TextField
          size="small"
          variant="outlined"
          label="Search"
          placeholder="Search simulations..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={classes.field}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {renderSelect("Function", "function", functionOptions)}
        {renderSelect("Creator", "creator", creatorOptions)}
        {renderSelect("Pass/Fail", "passExpections", getPassFailOptions())}
        {renderSelect("Sort By", "sortBy", getSortByOptions())}

        {activeFilterCount > 0 && (
          <Link
            component="button"
            variant="body2"
            className={classes.clearLink}
            onClick={clearAllFilters}
          >
            Clear All
          </Link>
        )}
      </Box>
    </Box>
  )
}

// Helper functions to extract options from simulations
function getFunctionOptions(simulations) {
  const options = [{ label: "All", value: "All" }]
  const uniqueFunctions = new Set()

  simulations.forEach((simulation) => {
    if (simulation.functionsInUse && Array.isArray(simulation.functionsInUse)) {
      simulation.functionsInUse.forEach((func) => {
        uniqueFunctions.add(func.name)
      })
    }
  })

  Array.from(uniqueFunctions)
    .sort()
    .forEach((func) => {
      options.push({
        label: func,
        value: func,
      })
    })

  return options
}

function getCreatorOptions(simulations) {
  const options = [{ label: "All", value: "All" }]
  const creators = new Set()

  simulations.forEach((simulation) => {
    if (simulation.createdByName) {
      creators.add(simulation.createdByName)
    }
  })

  Array.from(creators)
    .sort()
    .forEach((creator) => {
      options.push({
        label: creator,
        value: creator,
      })
    })

  return options
}

function getPassFailOptions() {
  return [
    { label: "All", value: "All" },
    { label: "Pass", value: "Pass" },
    { label: "Fail", value: "Fail" },
  ]
}

function getSortByOptions() {
  return [
    { label: "Name", value: "Name" },
    { label: "Created", value: "Created" }
  ]
}

// Filter and sort function
function applyFilters(simulations, filters, searchText) {
  let filtered = [...simulations]

  // Apply search filter
  if (searchText) {
    const lowerSearch = searchText.toLowerCase()
    filtered = filtered.filter(
      (sim) =>
        (sim.name && sim.name.toLowerCase().includes(lowerSearch)) ||
        (sim.createdByName &&
          sim.createdByName.toLowerCase().includes(lowerSearch)),
    )
  }

  // Apply function filter
  if (filters.function !== "All") {
    filtered = filtered.filter(
      (sim) =>
        sim.functionsInUse &&
        sim.functionsInUse.some((func) => func.name === filters.function),
    )
  }

  // Apply creator filter
  if (filters.creator !== "All") {
    filtered = filtered.filter((sim) => sim.createdByName === filters.creator)
  }

  // Apply pass/fail filter
  if (filters.passExpections !== "All") {
    filtered = filtered.filter((sim) => {
      const isPassing =
        sim.passExpections === true || sim.passExpections === "true"
      return filters.passExpections === "Pass" ? isPassing : !isPassing
    })
  }

  // Sort results
  switch (filters.sortBy) {
    case "Name":
      filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      break
    case "Created":
      filtered.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
      break
    default:
      break
  }

  return filtered
}
